import { paymentMethodsStore } from "@stores/payment-methods";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { PropsWithChildren, ReactElement, useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ICreatePaymentMethodFormData,
  ICreatePaymentMethodFormProps,
} from "./types";
import {
  Button,
  Card,
  Flex,
  Stack,
  NativeSelect,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { customerStore } from "@stores/customer";
import { DEFAULT_CUSTOMER_ADDRESS } from "@stores/customer/constants";
import SelectCountry from "@components/ui/form/SelectCountry";
import Form from "@common/components/ui/form/Form";
import ErrorMessage from "@common/components/text/ErrorMessage";
import FormLabel from "@common/components/ui/form/FormLabel";
import {
  getSelectItemsFromCountry,
  isAddressValidated,
  isStateFieldNeeded,
} from "@common/utils/data";

export default function CreatePaymentMethodForm({
  account,
}: PropsWithChildren<ICreatePaymentMethodFormProps>): ReactElement {
  const theme = useMantineTheme();

  // customer
  const {
    isLoading: loadingCustomerDetails,
    data: customerDetails,
    error: customerDetailsError,
    refetch: refetchCustomerDetails,
  } = customerStore.useGetDetails();

  // Payment methods
  const {
    isPending: loadingUpdatePaymentMethod,
    error: updatePaymentMethodError,
    mutate: updatePaymentMethod,
  } = paymentMethodsStore.useUpdatePaymentMethod();

  // Form
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<ICreatePaymentMethodFormData>({
    shouldFocusError: true,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: customerDetails?.billing_address || DEFAULT_CUSTOMER_ADDRESS,
  });
  const watchCountry = watch("country");

  useEffect(() => {
    if (account) {
      setValue("name", `${account.first_name} ${account.last_name}`);
    }
  }, [account, setValue]);

  // Stripe
  const stripe = useStripe();
  const elements = useElements();

  const handleStripeSubmit = useCallback(
    (formData: ICreatePaymentMethodFormData) => {
      if (!account || !stripe || !elements) {
        return;
      }

      // 1. Stripe related
      const card = elements.getElement(CardElement);
      if (!card) {
        console.error("CreatePaymentMethodForm, missing Stripe card element");
        return;
      }

      // 2. save customer PM
      const { name, ...address } = formData;
      updatePaymentMethod(
        {
          card,
          stripe,
          billing_details: {
            name,
            email: account.email,
            address,
          },
        },
        {
          onSuccess() {
            // 3. reload customer details
            refetchCustomerDetails();
          },
        },
      );
    },
    [stripe, account, elements, updatePaymentMethod, refetchCustomerDetails],
  );

  const showSpinner =
    loadingCustomerDetails ||
    loadingUpdatePaymentMethod ||
    !stripe ||
    !elements;

  return (
    <Form
      data-testid="CreatePaymentMethodForm"
      onSubmit={handleSubmit(handleStripeSubmit)}
      disabled={showSpinner}
    >
      <Stack spacing="sm">
        <TextInput
          id="name"
          label="Name on card"
          placeholder="Name on card"
          required
          {...register("name", { required: true })}
        />

        <Stack spacing={theme.spacing.xs / 2}>
          <FormLabel required>Credit card info</FormLabel>
          <Card id="stripe-form" withBorder p="sm">
            <CardElement
              options={{
                disableLink: true,
                hidePostalCode: true,
                style: {
                  base: {
                    fontFamily: theme.fontFamily,
                  },
                },
              }}
            />
          </Card>

          <ErrorMessage m={0} error={updatePaymentMethodError} />
        </Stack>

        <Flex
          align="flex-end"
          direction={{ base: "column", xs: "row" }}
          justify="flex-start"
          gap={theme.spacing.sm}
          w="100%"
        >
          <TextInput
            w="100%"
            id="street"
            label="Street address"
            placeholder="123 Main St"
            required
            {...register("street", { required: true })}
          />
          <TextInput
            w="100%"
            id="street2"
            placeholder="Unit number (optional)"
            {...register("street2")}
          />
        </Flex>

        <Controller
          name="country"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <SelectCountry
              data-testid="CreatePaymentMethodForm-country"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              mt={0}
            />
          )}
        />

        <Flex
          align="flex-start"
          direction={{ base: "column", xs: "row" }}
          justify="flex-start"
          gap={theme.spacing.lg}
          w="100%"
        >
          <TextInput
            w="100%"
            id="city"
            label="City"
            placeholder="City"
            defaultValue=""
            required
            {...register("city", { required: true })}
          />

          {isStateFieldNeeded(watchCountry) &&
            (isAddressValidated(watchCountry) ? (
              <Controller
                name="region"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <NativeSelect
                    w="100%"
                    data-testid="CreatePaymentMethodForm-region"
                    label="State"
                    data={getSelectItemsFromCountry(watchCountry)}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
            ) : (
              <TextInput
                w="100%"
                id="region"
                label="State"
                placeholder="State"
                {...register("region")}
              />
            ))}

          <TextInput
            w="100%"
            id="zip"
            label="ZIP Code"
            placeholder="Zipcode"
            maxLength={20}
            required
            {...register("zip", {
              required: true,
            })}
          />
        </Flex>
        <ErrorMessage m={0} error={errors.zip} />

        <ErrorMessage m={0} error={customerDetailsError} />
      </Stack>

      <Button
        type="submit"
        loading={showSpinner}
        loaderPosition="right"
        disabled={!isValid || showSpinner}
        mt="lg"
      >
        Save
      </Button>
    </Form>
  );
}
